import { defineMessages, useIntl } from 'react-intl'
import { sqftToAcres } from '../../../../helpers/converters'
import { isLandPropertyType } from '../../../../helpers/listings'
import { ClientEventSource, Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import {
  Flex,
  Icon,
  Text,
  Heading,
  Grid,
  Avatar,
  Card,
  CardHeader,
  CardBody
} from '@homebotapp/hb-react-component-catalog'
import { formatCurrency, formatNumber } from '../../../../helpers/formatters'
import { PrequalCtaButton } from '../Prequal/PrequalCtaButton'
import {
  selectCustomerFirstName,
  selectCustomerPhotoUri,
  selectIsSoloInsuranceAgent,
  selectIsSoloRea,
  selectScheduleACallUri
} from '../../../../store/selectors/customerProfile'
import { useSelector } from 'react-redux'
import { formatNumValue } from '../../../../helpers/utils'
import { SQFT_LOT_LIMIT } from '../../constants'
import { LeavingHomebotCtaButton } from '../../../shared/LeavingHomebotCta/LeavingHomebotCtaButton'
import { formatListingAddress } from '../../../../helpers/formatListingAddress'
import { useVerificationFlowEnabled } from '../../../../hooks/useVerificationFlowEnabled'
import { GenericVerificationDirectMessage } from '../../GenericVerificationDirectMessage'
import { CUSTOMER_MESSAGES } from '../../../../constants/customerMessages'
import { useCustomerBranding } from '../../../../hooks/homebotNetwork/usePreferredAgentOrCustomer'

const MSG = defineMessages({
  baths: {
    id: 'PropertyStats.baths',
    defaultMessage: '{num, plural, one {# bath} other {# baths}}'
  },
  beds: {
    id: 'PropertyStats.beds',
    defaultMessage: '{num, plural, one {# bed} other {# beds}}'
  },
  sqft: {
    id: 'PropertyStats.sqft',
    defaultMessage: '{num} sqft'
  },
  acres: {
    id: 'PropertyStats.acres',
    defaultMessage: '{num} acres'
  },
  propType: {
    id: 'PropertyStats.propertyType',
    defaultMessage: 'Property type'
  },
  year: {
    id: 'PropertyStats.yearBuilt',
    defaultMessage: 'Year built'
  },
  days: {
    id: 'PropertyStats.days',
    defaultMessage: '{num, plural, one {# day} other {# days}}'
  },
  time: {
    id: 'PropertyStats.timeOnMarket',
    defaultMessage: 'Time on market'
  },
  perSqft: {
    id: 'PropertyStats.pricePerSqft',
    defaultMessage: 'Price per sqft'
  },
  perAcre: {
    id: 'PropertyStats.pricePerAcre',
    defaultMessage: 'Price per acre'
  },
  financing: {
    id: 'PropertyStats.financing',
    defaultMessage: 'Have questions about financing?'
  },
  getPrequalified: {
    id: 'PropertyStats.getPrequalified',
    defaultMessage: 'Get Prequalified'
  },
  farm: {
    id: 'General.farm',
    defaultMessage: 'Farm'
  },
  land: {
    id: 'General.land',
    defaultMessage: 'Land'
  },
  house: {
    id: 'General.house',
    defaultMessage: 'House'
  },
  lot: {
    id: 'General.lot',
    defaultMessage: 'lot'
  },
  townhome: {
    id: 'General.townhome',
    defaultMessage: 'Townhome'
  },
  condo: {
    id: 'General.condo',
    defaultMessage: 'Condo'
  },
  manufactured: {
    id: 'General.manufactured',
    defaultMessage: 'Manufactured'
  },
  ['multi family']: {
    id: 'General.multifamily',
    defaultMessage: 'Multi Family'
  },
  other: {
    id: 'General.other',
    defaultMessage: 'Other'
  },
  ctaButtonText: {
    id: 'PropertyStats.scheduleACall',
    defaultMessage: 'Schedule a call'
  },
  continueText: {
    id: 'PropertyStats.continueText',
    defaultMessage: "Do you want to continue to {name}'s scheduling site in a new tab?"
  }
})

export interface PropertyStatsProps {
  listing?: Listing
}

export const PropertyStats = ({ listing }: PropertyStatsProps) => {
  const intl = useIntl()
  const scheduleACall = useSelector(selectScheduleACallUri)
  const propertyIsLand = isLandPropertyType(listing?.propertyType)
  const customerName = useSelector(selectCustomerFirstName)
  const branding = useCustomerBranding('LoanOfficer')
  const photoUri = branding?.photoUri
  const isSoloRea = useSelector(selectIsSoloRea)
  const isSoloInsuranceAgent = useSelector(selectIsSoloInsuranceAgent)
  const verificationFlowEnabled = useVerificationFlowEnabled()

  const bedBathSqftLotSize = () => {
    let str = ''

    if (listing?.bedroomsCount) {
      str += intl.formatMessage(MSG.beds, { num: listing.bedroomsCount })
    }

    if (listing?.bathroomsCount) {
      if (str?.length > 0) {
        str += ` • `
      }

      str += `${intl.formatMessage(MSG.baths, { num: listing.bathroomsCount })}`
    }

    if (listing?.sqftTotal) {
      if (str?.length > 0) {
        str += ` • `
      }

      str += `${intl.formatMessage(MSG.sqft, { num: formatNumber(listing.sqftTotal) })}`
    }

    if (listing?.sqftLot) {
      let value = formatNumValue(listing.sqftLot.toString())
      if (str?.length > 0) {
        str += ` • `
      }

      if (listing.sqftLot >= SQFT_LOT_LIMIT) {
        value = sqftToAcres(listing.sqftLot).toFixed(2)
        str += `${intl.formatMessage(MSG.acres, { num: value })} ${intl.formatMessage(MSG.lot)}`
      } else {
        str += `${intl.formatMessage(MSG.sqft, { num: value })} ${intl.formatMessage(MSG.lot)}`
      }
    }

    return str
  }

  const scheduleACallRenderModalBody = () => {
    return (
      <Text>
        {intl.formatMessage(MSG.continueText, {
          name: customerName
        })}
      </Text>
    )
  }

  const renderActionButtons = () => {
    if (verificationFlowEnabled) {
      return (
        <Flex w='100%' justify='center' align='center'>
          <GenericVerificationDirectMessage
            defaultTopicKey={CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topicKey}
            verifiedTopicKey={CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey}
            message='Can you help me get prequalified for this listing?'
            buttonText={intl.formatMessage(MSG.getPrequalified)}
            includePrequal={false}
            partnerType='LoanOfficer'
            buttonProps={{
              leftIcon: <Icon name='message' />,
              showAvatar: false,
              colorScheme: 'primary',
              variant: 'ghost',
              size: 'sm',
              tracking: {
                guid: '4eNAD74jG2c68Fntngs8eV',
                ui_context: 'ListingDetails - Property Stats',
                descriptor: 'Verification flow prequalification button clicked'
              }
            }}
          />
        </Flex>
      )
    }

    return (
      <>
        <PrequalCtaButton
          fullWidth
          ctaButtonColorScheme={'brand'}
          ctaButtonSize={'xs'}
          leftIcon={<Icon name='message' />}
          paddingY={4}
          listing={listing}
          alignItems={'center'}
          uiContext='Listing Details Page - Get Prequalified'
        >
          {intl.formatMessage(MSG.getPrequalified)}
        </PrequalCtaButton>

        {scheduleACall && (
          <LeavingHomebotCtaButton
            fullWidth
            scheduleACall
            hideIllustration
            eventSource={ClientEventSource.ListingDetailed}
            headerText={intl.formatMessage(MSG.ctaButtonText)}
            renderModalBody={scheduleACallRenderModalBody}
            tracking={{
              guid: '5vC99EmU3PPmrpGddabQf1',
              ui_context: 'ListingDetails - Property Stats',
              descriptor: 'Schedule a call button clicked'
            }}
            ctaButtonSize='xs'
            ctaButtonVariant='ghost'
            ctaButtonColorScheme='primary'
            paddingY={4}
            leftIcon={<Icon name='calendar' />}
            ctaLinkUrl={scheduleACall}
          >
            {intl.formatMessage(MSG.ctaButtonText)}
          </LeavingHomebotCtaButton>
        )}
      </>
    )
  }

  return (
    <>
      <Flex
        direction={['column', null, null, null, 'row']}
        alignItems={[null, null, null, null, 'center']}
        justifyContent={'space-between'}
      >
        <Flex direction={'column'}>
          <Heading as='h4' mb={0} size='xl' fontWeight='bold'>
            {/* @ts-ignore */}
            {formatCurrency(listing?.priceCents / 100)}
          </Heading>
          <Text mb={0} size='2xs' fontWeight='semibold'>
            {bedBathSqftLotSize()}
          </Text>
          {listing?.internetAddressDisplayYn !== false && (
            <Text size='2xs' fontWeight='semibold'>
              {formatListingAddress(listing?.address)}
            </Text>
          )}
        </Flex>
        {!isSoloRea && !isSoloInsuranceAgent && (
          <Card>
            <CardHeader pb={'0px !important'} pt={2}>
              <Flex direction={'row'} alignItems={'center'}>
                <Avatar height={8} width={8} src={photoUri} />
                <Text mb={0} ml={3} size='xs' fontWeight='bold'>
                  {intl.formatMessage(MSG.financing)}
                </Text>
              </Flex>
            </CardHeader>
            <CardBody pt={'0px !important'} pb={2}>
              <Flex justifyContent={'space-between'} alignItems={'center'} my={2}>
                {renderActionButtons()}
              </Flex>
            </CardBody>
          </Card>
        )}
      </Flex>
      <Grid mt={6} gap={4} templateColumns={['repeat(2, 1fr)', null, 'repeat(4, 1fr)']}>
        <Flex gap={2} align='center'>
          <Icon h='1.25rem' w='1.25rem' name='home' />
          <Flex direction='column'>
            {listing?.propertyType && (
              <Text mb={0} size='xs' color='neutral.900' fontWeight='semibold'>
                {intl.formatMessage(MSG[listing.propertyType.toLowerCase()])}
              </Text>
            )}
            {/* @ts-ignore */}
            <Text mb={0} size='2xs' lineHeight='.875rem'>
              {intl.formatMessage(MSG.propType)}
            </Text>
          </Flex>
        </Flex>
        {!!listing?.yearBuilt && (
          <Flex gap={2} align='center'>
            <Icon h='1.25rem' w='1.25rem' name='homeimprovement' />
            <Flex direction='column'>
              <Text mb={0} size='xs' color='neutral.900' fontWeight='semibold'>
                {listing.yearBuilt}
              </Text>
              {/* @ts-ignore */}
              <Text mb={0} size='2xs' lineHeight='.875rem'>
                {intl.formatMessage(MSG.year)}
              </Text>
            </Flex>
          </Flex>
        )}
        {!!listing?.daysOnMarket && (
          <Flex gap={2} align='center'>
            <Icon h='1.25rem' w='1.25rem' name='selling' />
            <Flex direction='column'>
              <Text mb={0} size='xs' color='neutral.900' fontWeight='semibold'>
                {intl.formatMessage(MSG.days, { num: listing?.daysOnMarket })}
              </Text>
              {/* @ts-ignore */}
              <Text mb={0} size='2xs' lineHeight='.875rem'>
                {intl.formatMessage(MSG.time)}
              </Text>
            </Flex>
          </Flex>
        )}
        {propertyIsLand && listing?.sqftLot && listing?.sqftLot > 0 ? (
          <Flex gap={2} align='center'>
            <Icon h='1.5rem' w='1.5rem' name='calculator' />
            <Flex direction='column'>
              <Text mb={0} size='xs' color='neutral.900' fontWeight='semibold'>
                {formatCurrency(Math.ceil(listing.priceCents / 100 / sqftToAcres(listing.sqftLot)))}
              </Text>
              {/* @ts-ignore */}
              <Text mb={0} size='2xs' lineHeight='.875rem'>
                {intl.formatMessage(MSG.perAcre)}
              </Text>
            </Flex>
          </Flex>
        ) : (
          !!listing?.sqftTotal &&
          listing.sqftTotal > 0 && (
            <Flex gap={2} align='center'>
              <Icon h='1.25rem' w='1.25rem' name='calculator' />
              <Flex direction='column'>
                <Text mb={0} size='xs' color='neutral.900' fontWeight='semibold'>
                  {formatCurrency(Math.ceil(listing?.priceCents / 100 / listing?.sqftTotal))}
                </Text>
                {/* @ts-ignore */}
                <Text mb={0} size='2xs' lineHeight='.875rem'>
                  {intl.formatMessage(MSG.perSqft)}
                </Text>
              </Flex>
            </Flex>
          )
        )}
      </Grid>
    </>
  )
}
